import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

interface AttachedFile {
    file: File;
    previewUrl?: SafeUrl;
    icon: string;
    fileType: string;
    id: string;
    isValid: boolean;
}

@Component({
    selector: 'app-file-attachment',
    templateUrl: './file-attachment.component.html',
    styleUrls: ['./file-attachment.component.scss'],
})
export class FileAttachmentComponent implements OnInit, OnChanges, OnDestroy {
    @Input() maxFiles = 10;

    @Input() maxFileSizeMB = 20;

    @Input() acceptedFileTypes = 'image/*,.pdf,.doc,.docx,.xls,.xlsx';

    @Input() initialFiles: File[] = [];

    @Input() hasError = false;

    @Input() validFiles: { [key: string]: boolean } = {};

    @Output() filesChanged = new EventEmitter<File[]>();

    attachedFileObjects: AttachedFile[] = [];

    private maxFileSize: number;

    private objectUrls: string[] = [];

    constructor(private sanitizer: DomSanitizer) {}

    ngOnInit(): void {
        this.maxFileSize = this.maxFileSizeMB * 1024 * 1024;
        this.processInitialFiles();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.initialFiles && !changes.initialFiles.firstChange) {
            this.processInitialFiles();
        }

        if (changes.validFiles && !changes.validFiles.firstChange) {
            this.updateFileValidityStatus();
        }
    }

    ngOnDestroy(): void {
        this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    }

    private generateUniqueId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
    }

    private updateFileValidityStatus(): void {
        let runningTotal = 0;
        const maxSizeMB = this.maxFileSizeMB;

        this.attachedFileObjects.forEach((fileObj) => {
            const fileSizeMB = fileObj.file.size / (1024 * 1024);
            runningTotal += fileSizeMB;

            fileObj.isValid = runningTotal <= maxSizeMB;
        });
    }

    private processInitialFiles(): void {
        this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
        this.objectUrls = [];
        this.attachedFileObjects = [];

        if (this.initialFiles && this.initialFiles.length > 0) {
            const validInitialFiles = this.initialFiles.slice(0, this.maxFiles);
            validInitialFiles.forEach((file) => {
                this.addFileToList(file);
            });
            this.updateFileValidityStatus();
            this.emitAttachedFiles();
        }
    }

    private addFileToList(file: File): boolean {
        if (file.size > this.maxFileSize) {
            console.warn(`File "${file.name}" exceeds the size limit of ${this.maxFileSizeMB}MB.`);
            return false;
        }

        if (!this.isFileTypeAccepted(file)) {
            console.warn(`File type for "${file.name}" is not accepted.`);
            return false;
        }

        const fileType = this.determineFileType(file);
        const fileId = this.generateUniqueId();

        const fileObject: AttachedFile = {
            file,
            icon: this.getFileIcon(file, fileType),
            fileType,
            previewUrl: undefined,
            id: fileId,
            isValid: true,
        };

        if (file.type.startsWith('image/')) {
            const url = URL.createObjectURL(file);
            this.objectUrls.push(url);
            fileObject.previewUrl = this.sanitizer.bypassSecurityTrustUrl(url);
        }

        this.attachedFileObjects.push(fileObject);
        return true;
    }

    private determineFileType(file: File): string {
        const filename = file.name.toLowerCase();
        const mimeType = file.type.toLowerCase();

        if (mimeType.startsWith('image/')) {
            return 'image';
        }

        if (filename.endsWith('.xls') || filename.endsWith('.xlsx') || filename.endsWith('.csv') || filename.endsWith('.xlsm')) {
            return 'excel';
        }

        if (
            mimeType.includes('spreadsheet') ||
            mimeType.includes('excel') ||
            mimeType.includes('sheet') ||
            mimeType === 'application/vnd.ms-excel' ||
            mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
            return 'excel';
        }

        if (filename.endsWith('.pdf') || mimeType.includes('pdf')) {
            return 'pdf';
        }

        if (
            filename.endsWith('.doc') ||
            filename.endsWith('.docx') ||
            mimeType.includes('word') ||
            mimeType.includes('document') ||
            mimeType === 'application/msword' ||
            mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) {
            return 'word';
        }

        return 'other';
    }

    private isFileTypeAccepted(file: File): boolean {
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
        const acceptedTypesArray = this.acceptedFileTypes.split(',');

        return acceptedTypesArray.some((type) => {
            const trimmedType = type.trim().toLowerCase();
            if (trimmedType.startsWith('.')) {
                return fileExtension === trimmedType;
            }
            if (trimmedType.endsWith('/*')) {
                const baseMime = trimmedType.replace('/*', '');
                return file.type.startsWith(baseMime);
            }
            return file.type === trimmedType;
        });
    }

    onFileSelected(event: any): void {
        const { files } = event.target;
        let fileAdded = false;
        if (files) {
            Array.from(files).forEach((file) => {
                if (this.attachedFileObjects.length < this.maxFiles) {
                    if (this.addFileToList(file as File)) {
                        fileAdded = true;
                    }
                }
            });

            event.target.value = null;
            if (fileAdded) {
                this.updateFileValidityStatus();
                this.checkForExceedingLimit();
                this.emitAttachedFiles();
            }
        }
    }

    removeFile(index: number): void {
        const removedFile = this.attachedFileObjects.splice(index, 1)[0];

        if (this.attachedFileObjects.length > 0) {
            this.updateFileValidityStatus();
            this.checkForExceedingLimit();
        } else {
            this.hasError = false;
        }

        if (removedFile?.previewUrl) {
            const urlString = this.sanitizer.sanitize(4, removedFile.previewUrl)?.toString();
            if (urlString?.startsWith('blob:')) {
                URL.revokeObjectURL(urlString);
                const urlIndex = this.objectUrls.indexOf(urlString);
                if (urlIndex > -1) {
                    this.objectUrls.splice(urlIndex, 1);
                }
            }
        }

        this.emitAttachedFiles();
    }

    private checkForExceedingLimit(): void {
        const totalSize = this.attachedFileObjects.reduce((sum, fileObj) => {
            return sum + fileObj.file.size / (1024 * 1024);
        }, 0);

        this.hasError = totalSize > this.maxFileSizeMB;
    }

    private emitAttachedFiles(): void {
        this.filesChanged.emit(this.attachedFileObjects.map((fo) => fo.file));
    }

    triggerFileInput(fileInput: HTMLInputElement): void {
        fileInput.click();
    }

    formatFileSize(size: number): string {
        if (size === 0) {
            return '0 KB';
        }
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(size) / Math.log(k));
        return `${parseFloat((size / k ** i).toFixed(1))} ${sizes[i]}`;
    }

    private getFileIcon(file: File, fileType: string): string {
        switch (fileType) {
            case 'image':
                return 'icon-image';
            case 'pdf':
                return 'icon-pdf';
            case 'word':
                return 'icon-doc';
            case 'excel':
                return 'icon-excel';
            default:
                return 'icon-files';
        }
    }

    isImageFile(fileType: string): boolean {
        return fileType === 'image';
    }

    isPdfFile(fileType: string): boolean {
        return fileType === 'pdf';
    }

    isWordFile(fileType: string): boolean {
        return fileType === 'word';
    }

    isExcelFile(fileType: string): boolean {
        return fileType === 'excel';
    }
}
